/**
 * Inbound-link hierarchy for unit pages — TypeScript port of the pipeline's deterministic rule engine
 * (manual-qa/link-hierarchy-20261026/pipeline/policy_rules.py::_unit, METHOD.md §4 "UNIT pair" / "UNIT hub").
 *
 * Every list here is derived from data/link-hierarchy/unit-link-policy.json (exported from policy.json by
 * pipeline/export_app_policy.py — ranked pools, never a hand-picked page) plus the page's own route data.
 * Per-page variation is a crc32(source url) rotation over a pool, identical to pipeline/lh.py::rotate, so the
 * Python model and the built HTML agree link for link (impl/assert_unit_html.py checks that).
 *
 * Blocks (data-block attribute = block name):
 *  - popular-unit    B_UNIT_POPULAR_SAME_MEASURE UNIT-HV pairs of the page's measure by rank, then rotation over all UNIT-HV
 *  - neighbours      X->Y' for the UNIT_NEIGHBOURS units adjacent to Y in the measure's unit order + X->base unit
 *  - group-links     HV source: whole from-unit group (HV first, neighbours, rest) capped at UNIT_TABLE_LINK_CAP;
 *                    LV source: HV members of the group only (neighbours are their own block, other rows plain text)
 *  - group-list      base pair (X->base unit) keeps the whole from-unit group (index exemption; the group's mini-hub)
 *  - file-tools      FP_TOP_FIXED fixed FP-T1 + R_FP_UNIT_TIME rotation over FP-T1 u FP-T2 (minus the fixed six)
 *  - more-converters R_TIME_ON_UNIT rotation over TIME-HV
 *  - hub-list        hub pages: HV first, then every pair by from-unit group (<= CAP_INDEX) or one base pair per from-unit
 * Pure functions, no I/O: the loader lives in link-hierarchy.server.ts (build time only).
 */

export interface PolicyEntry { url: string; h1: string; measure?: string; rank?: number }

export interface UnitLinkPolicy {
    meta: { generated: string; source: string; config: { [k: string]: number } };
    unit_hv: PolicyEntry[];                              // ranked by rank_key (rank_in_family asc)
    unit_hv_by_measure: { [measure: string]: string[] }; // urls, ranked
    unit_base_unit_by_measure: { [measure: string]: string };
    unit_order_by_measure: { [measure: string]: string[] };
    fp_top6: PolicyEntry[];
    fp_rotation_pool: PolicyEntry[];
    time_hv: PolicyEntry[];
    unit_hub_lists: { [hubUrl: string]: string[] };
    unit_base_pairs: string[];
}

/** One rendered link: site path (e.g. /unit-converter/x, /video-compressor, /time-converter/...) + anchor (= target H1). */
export interface LinkTarget { href: string; text: string }

/** A member of the page's from-unit group (from sidePanelLinks / fromTypeSupportedTargetConverters). */
export interface GroupMember {
    toUnit: string;          // unit abbreviation key, e.g. "m3/h"
    url: string;             // site path, e.g. /unit-converter/gallons-per-minute-to-cubic-meters-per-hour
    h1: string;              // "Gallons per minute (gal/min) to Cubic meters per hour (m3/h) conversion"
}

export interface UnitPairPage {
    url: string;             // /unit-converter/{from}-to-{to}
    measure: string;         // from.type
    fromUnit: string;        // from.unit
    toUnit: string;          // to.unit
    group: GroupMember[];    // the from-unit group in generator order, self excluded
}

export type UnitTier = 'UNIT-HV' | 'UNIT-LV';

export interface UnitPairBlocks {
    tier: UnitTier;
    isBasePair: boolean;
    popularUnit: LinkTarget[];
    neighbours: LinkTarget[];
    groupLinks: string[];      // urls (rendered with the existing side-panel/pill formatting)
    groupList: string[];       // urls, base pairs only
    fileTools: LinkTarget[];
    moreConverters: LinkTarget[];
    /** Every in-app pair url this page may link from its conversion table / side panel / inline content. */
    linked: string[];
}

export interface UnitHubBlocks {
    hubList: string[];
    fileTools: LinkTarget[];
    moreConverters: LinkTarget[];
}

// ---------------------------------------------------------------------------------------------------------
// crc32 (IEEE 802.3, same polynomial/table as zlib.crc32) over the UTF-8 bytes of the key — parity with Python
// zlib.crc32(url.encode('utf-8')) & 0xffffffff is asserted in test/link-hierarchy.test.ts.
// ---------------------------------------------------------------------------------------------------------
let CRC_TABLE: Uint32Array | null = null;
function crcTable(): Uint32Array {
    if (CRC_TABLE) return CRC_TABLE;
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        t[n] = c >>> 0;
    }
    CRC_TABLE = t;
    return t;
}

function utf8Bytes(s: string): number[] {
    const out: number[] = [];
    for (let i = 0; i < s.length; i++) {
        let cp = s.charCodeAt(i);
        if (cp >= 0xd800 && cp <= 0xdbff && i + 1 < s.length) {
            const lo = s.charCodeAt(i + 1);
            if (lo >= 0xdc00 && lo <= 0xdfff) { cp = 0x10000 + ((cp - 0xd800) << 10) + (lo - 0xdc00); i++; }
        }
        if (cp < 0x80) out.push(cp);
        else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
        else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
        else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    }
    return out;
}

export function crc32(s: string): number {
    const t = crcTable();
    let c = 0xffffffff;
    for (const b of utf8Bytes(s)) c = t[(c ^ b) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
}

/** Deterministic even spread (lh.py::rotate): offset = crc32(key) mod |pool|; take the next k pool members not excluded. */
export function rotate<T>(pool: T[], key: string, k: number, exclude: Set<T> = new Set()): T[] {
    if (!pool.length || k <= 0) return [];
    const off = crc32(key) % pool.length;
    const out: T[] = [];
    for (let i = 0; i < pool.length; i++) {
        const p = pool[(off + i) % pool.length];
        if (exclude.has(p)) continue;
        out.push(p);
        if (out.length >= k) break;
    }
    return out;
}

/** policy_rules.Policy._emit: dedupe in order, drop empties and the excluded (self) url. */
function emit(targets: (string | undefined | null)[], exclude: string[] = []): string[] {
    const seen = new Set(exclude);
    const out: string[] = [];
    for (const t of targets) {
        if (!t || seen.has(t)) continue;
        seen.add(t);
        out.push(t);
    }
    return out;
}

// ---------------------------------------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------------------------------------
export function unitTier(policy: UnitLinkPolicy, url: string): UnitTier {
    return hvSet(policy).has(url) ? 'UNIT-HV' : 'UNIT-LV';
}

const hvSetCache = new WeakMap<UnitLinkPolicy, Set<string>>();
function hvSet(policy: UnitLinkPolicy): Set<string> {
    let s = hvSetCache.get(policy);
    if (!s) { s = new Set(policy.unit_hv.map(e => e.url)); hvSetCache.set(policy, s); }
    return s;
}
const hvUrlsCache = new WeakMap<UnitLinkPolicy, string[]>();
function hvUrls(policy: UnitLinkPolicy): string[] {
    let a = hvUrlsCache.get(policy);
    if (!a) { a = policy.unit_hv.map(e => e.url); hvUrlsCache.set(policy, a); }
    return a;
}
const h1Cache = new WeakMap<UnitLinkPolicy, Map<string, string>>();
function h1Map(policy: UnitLinkPolicy): Map<string, string> {
    let m = h1Cache.get(policy);
    if (!m) {
        m = new Map();
        for (const e of [...policy.unit_hv, ...policy.fp_top6, ...policy.fp_rotation_pool, ...policy.time_hv]) m.set(e.url, e.h1);
        h1Cache.set(policy, m);
    }
    return m;
}
function targets(policy: UnitLinkPolicy, urls: string[]): LinkTarget[] {
    const h1 = h1Map(policy);
    return urls.map(u => ({ href: u, text: h1.get(u) || u }));
}

/** policy_rules.Policy.unit_neighbours */
export function unitNeighbours(policy: UnitLinkPolicy, page: UnitPairPage): string[] {
    const { measure: m, fromUnit: fu, toUnit: tu } = page;
    const order = policy.unit_order_by_measure[m] || [];
    const n = policy.meta.config.UNIT_NEIGHBOURS;
    const byTo = new Map(page.group.map(g => [g.toUnit, g.url] as [string, string]));
    const out: string[] = [];
    const i = order.indexOf(tu);
    if (i >= 0) {
        const cand: string[] = [];
        for (let d = 1; d < order.length; d++) {
            for (const j of [i - d, i + d]) {
                if (j >= 0 && j < order.length && order[j] !== fu && cand.length < n) cand.push(order[j]);
            }
            if (cand.length >= n) break;
        }
        for (const x of cand) { const t = byTo.get(x); if (t) out.push(t); }
    }
    const b = policy.unit_base_unit_by_measure[m];
    if (b && b !== fu && b !== tu) { const t = byTo.get(b); if (t) out.push(t); }
    return out;
}

/** policy_rules.Policy.unit_is_base_pair */
export function unitIsBasePair(policy: UnitLinkPolicy, measure: string, fromUnit: string, toUnit: string): boolean {
    const b = policy.unit_base_unit_by_measure[measure];
    if (b === undefined || b === null) return false;
    if (fromUnit !== b) return toUnit === b;
    const others = (policy.unit_order_by_measure[measure] || []).filter(x => x !== b);
    return others.length > 0 && toUnit === others[0];
}

/** The File tools row + More converters slot shared by every unit page type (policy_rules.Policy._unit_file_tools). */
export function unitFileTools(policy: UnitLinkPolicy, url: string): { fileTools: LinkTarget[]; moreConverters: LinkTarget[] } {
    const c = policy.meta.config;
    const fixed = policy.fp_top6.map(e => e.url);
    const rot = rotate(policy.fp_rotation_pool.map(e => e.url), url, c.R_FP_UNIT_TIME);
    return {
        fileTools: targets(policy, emit([...fixed, ...rot])),
        moreConverters: targets(policy, emit(rotate(policy.time_hv.map(e => e.url), url, c.R_TIME_ON_UNIT))),
    };
}

/** policy_rules.Policy._unit, page_type == 'pair' (contextual + rotation blocks; header/footer/breadcrumb/reverse unchanged). */
export function unitPairBlocks(policy: UnitLinkPolicy, page: UnitPairPage): UnitPairBlocks {
    const c = policy.meta.config;
    const u = page.url;
    const hv = hvSet(policy);
    const tier = unitTier(policy, u);
    // Raw neighbour list as policy_rules.unit_neighbours returns it (the base unit may repeat an adjacent unit); the
    // rendered block is the deduped list, but group-links below must use the raw list — see the cap note there.
    const nbRaw = unitNeighbours(policy, page);
    const nb = emit(nbRaw, [u]);
    // popular-unit: same-measure HV first, then rotation over all HV
    const same = (policy.unit_hv_by_measure[page.measure] || []).filter(x => x !== u).slice(0, c.B_UNIT_POPULAR_SAME_MEASURE);
    const fill = rotate(hvUrls(policy), u, c.B_UNIT_POPULAR - same.length, new Set([...same, u]));
    const popular = emit([...same, ...fill]);
    // group blocks — the from-unit group in the measure's unit order (enumerate_pages.py sorts by the to-unit's index in
    // unit_order, then url), not in the route data's own order: the two differ for a few volumeFlowRate pages and the
    // UNIT_TABLE_LINK_CAP makes that visible.
    const order = policy.unit_order_by_measure[page.measure] || [];
    const idx = (g: GroupMember) => { const i = order.indexOf(g.toUnit); return i < 0 ? 999 : i; };
    const group = [...page.group].sort((a, b) => idx(a) - idx(b) || (a.url < b.url ? -1 : a.url > b.url ? 1 : 0)).map(g => g.url).filter(x => x !== u);
    const hvInGroup = group.filter(x => hv.has(x));
    const isBase = unitIsBasePair(policy, page.measure, page.fromUnit, page.toUnit);
    let groupLinks: string[] = [];
    let groupList: string[] = [];
    if (tier === 'UNIT-HV') {
        const hvs = new Set(hvInGroup), nbs = new Set(nbRaw);
        const rest = group.filter(x => !hvs.has(x) && !nbs.has(x));
        // policy_rules.py: (hv_in_group + nb + rest)[:UNIT_TABLE_LINK_CAP] then _emit dedupes — the cap is applied to the
        // raw concatenation, so a duplicated neighbour (or an HV neighbour) leaves 43–44 links, not 45. Mirrored exactly.
        groupLinks = emit([...hvInGroup, ...nbRaw, ...rest].slice(0, c.UNIT_TABLE_LINK_CAP), [u]);
    } else if (isBase) {
        groupList = emit(group, [u]);
    } else {
        const nbs = new Set(nb);
        groupLinks = emit(hvInGroup.filter(x => !nbs.has(x)), [u]);
    }
    const { fileTools, moreConverters } = unitFileTools(policy, u);
    const linked = emit([...groupLinks, ...groupList, ...nb]);
    return { tier, isBasePair: isBase, popularUnit: targets(policy, popular), neighbours: nbTargets(page, nb), groupLinks, groupList, fileTools, moreConverters, linked };
}

function nbTargets(page: UnitPairPage, urls: string[]): LinkTarget[] {
    const byUrl = new Map(page.group.map(g => [g.url, g.h1] as [string, string]));
    return urls.map(u => ({ href: u, text: byUrl.get(u) || u }));
}

/** policy_rules.Policy._unit, page_type == 'hub': the ordered hub list comes from policy.json (unit.hub_lists). */
export function unitHubBlocks(policy: UnitLinkPolicy, hubUrl: string): UnitHubBlocks {
    return { hubList: policy.unit_hub_lists[hubUrl] || [], ...unitFileTools(policy, hubUrl) };
}

/** Unit pair H1 as the pipeline defines it (enumerate_pages.py): "From (abbr) to To (abbr) conversion". */
export function unitPairH1(fromPlural: string, fromAbbr: string, toPlural: string, toAbbr: string): string {
    return `${fromPlural} (${fromAbbr}) to ${toPlural} (${toAbbr}) conversion`;
}

/**
 * Unlink in-app anchors the policy does not allow on this page (generator-injected FAQ/HowTo links to the from-unit
 * group would otherwise re-add LV->LV anchors). External links and allowed targets are left untouched.
 */
export function unlinkDisallowedInternalAnchors(html: string | undefined | null, allowed: Set<string>): string {
    if (!html) return html || '';
    return html.replace(/<a\b([^>]*)\bhref=(["'])([^"']*)\2([^>]*)>([\s\S]*?)<\/a>/gi, (m, _pre, _q, href, _post, inner) => {
        const path = href.replace(/^https?:\/\/(www\.)?xconvert\.com/, '').split('?')[0].split('#')[0].replace(/\/$/, '');
        if (!path.startsWith('/unit-converter/')) return m;      // external or non-unit link: untouched
        return allowed.has(path) ? m : inner;
    });
}
